/**
 * 2026-11-01 (owner) · Completed → Material Balance, in a modal on the page (the /bal-material page is retired): each
 * material's expected quantity against what was weighed, each in its own unit. A q.s. line states no quantity.
 */
import React, { useEffect, useState } from 'react';
import { Scale } from 'lucide-react';
import { Alert, Button } from 'antd';
import DataTable from '@/components/DataTable';
import PageModal from '@/components/PageModal';
import { get_material_balance } from '@/utils/api';

const quantity = (v, r) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })}${r?.uom ? ` ${r.uom}` : ''}`;
const TYPE_WORDS = { API: 'API', PRIMARY_PM: 'Primary PM', SECONDARY_PM: 'Secondary PM', PH_ADJUST: 'pH adjuster' };
const typeWord = (t) => TYPE_WORDS[t] || (t || '—').toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const columns = [
  { title: 'Material', dataIndex: 'material_name', key: 'material_name' },
  { title: 'Type', dataIndex: 'material_type', key: 'material_type', width: 150, render: typeWord },
  { title: 'Expected quantity', dataIndex: 'exp_quantity', key: 'exp_quantity', width: 170,
    render: (v, r) => (v === null || v === undefined ? 'q.s.' : quantity(v, r)) },
  { title: 'Actual quantity', dataIndex: 'actual_qty', key: 'actual_qty', width: 170, render: quantity },
];

export default function MaterialBalanceModal({ batchNumber, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!batchNumber) return undefined;
    let live = true;
    setLoading(true); setRows([]); setFailed(false);
    get_material_balance({ batch_number: batchNumber }, (res) => {
      if (!live) return;
      if (res?.data?.status === 'success') setRows(res.data.data || []);
      else setFailed(true);
      setLoading(false);
    });
    return () => { live = false; };
  }, [batchNumber, attempt]);

  return (
    <PageModal
      open={!!batchNumber}
      onCancel={onClose}
      icon={<Scale size={22} />}
      title={`Material balance · ${batchNumber || ''}`}
      description="Each material's expected quantity against what was weighed."
      width={860}
      destroyOnHidden
    >
      {failed ? (
        <Alert type="error" showIcon message="The material balance could not be read."
               action={<Button size="small" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>} />
      ) : (
        <DataTable bordered pagination={false} height={null} showCount={false}
                   rowKey={(r) => `${r.materail_id}-${r.material_name}`}
                   loading={loading} columns={columns} data={rows}
                   locale={{ emptyText: 'No materials on this batch' }} />
      )}
    </PageModal>
  );
}
